/**
 * The speaker of a live call: the model's PCM16 frames, played as one voice.
 *
 * The phone's `live-audio.ts` schedules each frame on its `AudioContext`'s clock
 * at the later of "now plus a lead" and where the previous frame ended, so a
 * hundred frames arriving in bursts still play without a seam. This is that
 * queue over the browser's own `AudioContext`, and the slot is worked out by the
 * phone's `playbackSlot` (`@/lib/voice-level`), the same rule the orb's level
 * follows — so what is drawn and what is heard cannot drift apart.
 *
 * **The lead is the jitter buffer.** A frame that finds the pipe empty — the
 * first of a turn, or one that arrived after the last had finished — is put
 * `PLAYER_LEAD_MS` ahead of now, and every frame behind it goes on the end of
 * the one before. A socket over the internet delivers late and in bunches; a
 * tenth of a second of lead is what turns that into a voice with no gaps, at
 * the price of a tenth of a second before the first word. The phone's lead is
 * 20 ms, for a socket that is usually on the same network as its server.
 *
 * `nextStart` is also the answer to "is the model still talking": while the
 * context's clock has not reached it, there is sound in the pipe. Its end is
 * heard from the sources themselves (`onended`), not a timer, so a hidden
 * tab, whose timers the browser slows to a crawl, still hears the voice
 * stop on time. `flush` is what the person cutting in costs: every scheduled
 * source stops.
 *
 * Live mode's ear (step 4.4): `duck`/`unduck` ramp the voice's gain down
 * within 150 ms and back (`duckRampOf`), `playedMs` is how much of OS's
 * line has been heard (the slots scheduled since the line's first frame,
 * which `newLine` marks on the server's `speaking`, less what is still ahead
 * of `currentTime`), and the context's state changes
 * (Safari's `interrupted`) are told to `onStateChange`.
 */
import { duckRampOf, LIVE_OUTPUT_RATE, playedMsOf, type LiveSound } from '@alluzo/shared';

import { pcm16ToFloat } from '@/lib/live-pcm';
import { playbackSlot } from '@/lib/voice-level';

import { EARCONS, earconMs } from './earcons';

/** How far ahead of now a frame that finds the pipe empty is scheduled. */
export const PLAYER_LEAD_MS = 100;

/**
 * Where one frame goes, and where the pipe ends after it, on any clock in
 * milliseconds. A pipe still playing takes the frame on its end, with no lead —
 * a lead there would be a gap in the middle of a word. A pipe that has run dry
 * takes the lead again. The orb's level follows the same slots on `Date.now()`.
 */
export const frameSlot = (nowMs: number, playheadMs: number, frameMs: number): { readonly atMs: number; readonly playheadMs: number } =>
  playheadMs > nowMs ? playbackSlot(nowMs, playheadMs, frameMs, 0) : playbackSlot(nowMs, 0, frameMs, PLAYER_LEAD_MS);

export type PcmPlayer = {
  /** Make the context, or wake it. Call it from a click where one is at hand. */
  readonly open: () => Promise<void>;
  /** One binary frame from the server: PCM16 little-endian mono at the output rate. */
  readonly play: (frame: Uint8Array) => void;
  /** The person cut in: drop every frame that has not been heard yet. */
  readonly flush: () => void;
  /** Whether there is still sound scheduled, read at this instant. */
  readonly isSpeaking: () => boolean;
  /** Told when that answer changes. One listener; a new one replaces the last. */
  readonly onSpeakingChange: (listener: ((speaking: boolean) => void) | null) => void;
  /**
   * How loud the voice plays, 0 to 1. Sound off is 0: the frames still play
   * out on time, so `isSpeaking` and every timing built on it stay the same
   * (os-live-mode.md, Decided 8). Kept across `close` and `open`.
   */
  readonly setVolume: (volume: number) => void;
  /**
   * One of live mode's earcons, on this context beside the voice: not in its
   * queue (Interrupt never cuts it, and it is never OS speaking), and at full
   * volume whatever the voice's. Nothing before `open`.
   */
  readonly earcon: (sound: LiveSound) => void;
  /** Stop everything and let the context go, once an earcon still sounding has finished. The next `open` makes a new one. */
  readonly close: () => Promise<void>;
  /** How much of OS's current line has been heard, in ms, counted from its first frame. */
  readonly playedMs: () => number;
  /** The server began a new line of OS's (`speaking`): the next frame starts `playedMs` again. */
  readonly newLine: () => void;
  /** Turn the voice down to the ducked gain within `DUCK_MS` (someone is talking over it). */
  readonly duck: () => void;
  /** Bring the voice back to the person's volume. */
  readonly unduck: () => void;
  /** Told the context's state when it changes (`interrupted` on Safari). One listener. */
  readonly onStateChange: (listener: ((state: string) => void) | null) => void;
};

const makeContext = (): AudioContext => {
  try {
    return new AudioContext({ sampleRate: LIVE_OUTPUT_RATE, latencyHint: 'interactive' });
  } catch {
    // The buffers carry their own rate, so a context at the device's rate plays them as well.
    return new AudioContext({ latencyHint: 'interactive' });
  }
};

export const createPcmPlayer = (rate: number = LIVE_OUTPUT_RATE): PcmPlayer => {
  let context: AudioContext | null = null;
  /** Every frame plays through this, so Sound off is one value and never a missed frame. */
  let gain: GainNode | null = null;
  let volume = 1;
  /** Everything scheduled and not yet finished, so cutting in can stop it. */
  let sources: AudioBufferSourceNode[] = [];
  /** Where the next frame goes on the context's clock, in milliseconds; 0 is an empty pipe. */
  let nextStartMs = 0;
  let speaking = false;
  let listener: ((speaking: boolean) => void) | null = null;
  let stateListener: ((state: string) => void) | null = null;
  /** OS's line as scheduled since its first frame, in milliseconds; a pipe that runs dry mid-line keeps counting. */
  let turnScheduledMs = 0;
  /** The next frame is a line's first: after `newLine`, a flush, and at the start. */
  let lineFresh = true;
  /** Someone talks over the voice: it plays at the ducked gain. */
  let ducked = false;
  /** When the last earcon ends, on `Date.now()`'s clock. */
  let earconUntil = 0;

  const setSpeaking = (next: boolean): void => {
    if (speaking === next) return;
    speaking = next;
    listener?.(next);
  };

  /** What has been heard of the turn, read on the context's clock. */
  const heardMs = (): number => {
    const held = context;
    return held === null ? 0 : playedMsOf({ scheduledMs: turnScheduledMs, aheadMs: nextStartMs - held.currentTime * 1000 });
  };

  /** The voice's gain to the person's volume, ducked or not, by a ramp. */
  const applyGain = (): void => {
    const held = context;
    if (held === null || gain === null) return;
    const ramp = duckRampOf(ducked, volume);
    gain.gain.cancelScheduledValues(held.currentTime);
    gain.gain.setTargetAtTime(ramp.target, held.currentTime, ramp.timeConstantS);
  };

  const flush = (): void => {
    // What was heard stays the line's `playedMs` until the next line begins.
    turnScheduledMs = heardMs();
    lineFresh = true;
    for (const source of sources) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        /* Already finished. */
      }
      try {
        source.disconnect();
      } catch {
        /* Already gone. */
      }
    }
    sources = [];
    nextStartMs = 0;
    setSpeaking(false);
  };

  const open = async (): Promise<void> => {
    if (context === null) {
      context = makeContext();
      gain = context.createGain();
      gain.gain.value = volume;
      gain.connect(context.destination);
      ducked = false;
      const made = context;
      made.onstatechange = () => stateListener?.(made.state);
    }
    if (context.state === 'suspended') await context.resume();
  };

  const play = (frame: Uint8Array): void => {
    const held = context;
    if (held === null) return;
    const samples = pcm16ToFloat(frame);
    if (samples.length === 0) return;
    const buffer = held.createBuffer(1, samples.length, rate);
    buffer.copyToChannel(samples, 0);
    const source = held.createBufferSource();
    source.buffer = buffer;
    source.connect(gain ?? held.destination);
    const nowMs = held.currentTime * 1000;
    // A line's first frame starts `playedMs` again; its gaps are not heard, and not counted.
    if (lineFresh) turnScheduledMs = 0;
    lineFresh = false;
    const slot = frameSlot(nowMs, nextStartMs, buffer.duration * 1000);
    source.start(slot.atMs / 1000);
    nextStartMs = slot.playheadMs;
    turnScheduledMs += buffer.duration * 1000;
    sources.push(source);
    // A frame heard is not one cutting in can stop any more; the last one
    // heard is the voice stopping, told by the source itself, not a timer.
    source.onended = () => {
      sources = sources.filter((one) => one !== source);
      if (sources.length === 0 && context === held) {
        nextStartMs = 0;
        setSpeaking(false);
      }
    };
    setSpeaking(true);
  };

  const earcon = (sound: LiveSound): void => {
    const held = context;
    if (held === null) return;
    const start = held.currentTime;
    for (const note of EARCONS[sound]) {
      const tone = held.createOscillator();
      const envelope = held.createGain();
      tone.type = 'sine';
      tone.frequency.value = note.hz;
      const from = start + note.atMs / 1000;
      const to = from + note.ms / 1000;
      envelope.gain.setValueAtTime(0, from);
      envelope.gain.linearRampToValueAtTime(note.gain, from + 0.01);
      envelope.gain.exponentialRampToValueAtTime(0.0001, to);
      tone.connect(envelope);
      envelope.connect(held.destination);
      tone.start(from);
      tone.stop(to + 0.02);
    }
    earconUntil = Math.max(earconUntil, Date.now() + earconMs(sound) + 40);
  };

  const close = async (): Promise<void> => {
    flush();
    // The falling chime plays out before the context goes.
    const left = earconUntil - Date.now();
    if (left > 0) await new Promise((resolve) => setTimeout(resolve, left));
    const held = context;
    context = null;
    gain = null;
    ducked = false;
    if (held === null) return;
    held.onstatechange = null;
    try {
      await held.close();
    } catch {
      /* Closed either way. */
    }
  };

  return {
    open,
    play,
    flush,
    isSpeaking: () => speaking,
    onSpeakingChange: (next) => {
      listener = next;
    },
    setVolume: (next) => {
      volume = Math.min(1, Math.max(0, next));
      if (gain === null) return;
      if (ducked) applyGain();
      else gain.gain.value = volume;
    },
    earcon,
    close,
    playedMs: heardMs,
    newLine: () => {
      lineFresh = true;
    },
    duck: () => {
      if (ducked) return;
      ducked = true;
      applyGain();
    },
    unduck: () => {
      if (!ducked) return;
      ducked = false;
      applyGain();
    },
    onStateChange: (next) => {
      stateListener = next;
    },
  };
};
